import { UpdateActionInterface } from '@omni/blocks';
import { useBlockPageContext } from '@omni/blocks/BlockPageContext';
import { useShellContext } from '@omni/kit';
import { EndUserClaims } from '@omni/kit/auth/Service/Types';
import { KitButton, KitSnack, KitText } from '@omni/kit/components';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import { SUBSPLASH_PHONE_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import useShellAuthProviders from '@omni/kit/contexts/ShellContext/hooks/useShellAuthProviders';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BaseService from '@omni/kit/services/BaseService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { notifyAppUserSettingsUpdated } from '@omni/kit/utilities/NativeHelpers';
import formatPhoneNumberByRegion from '@omni/kit/utilities/formatPhoneNumberByRegion';
import { decode } from 'base-64';
import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

export interface RemovePhoneLoginModalProps {
  appKey: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  /** The action to perform when a user delete their phone number. */
  updatePhoneAction?: UpdateActionInterface;
  setRefreshing?: (refreshing: boolean) => void;
  verifiedPhoneNumber: {
    significant: string;
    country: { region_code: string; calling_code: string };
  };
  handleLogOut: () => void;
}

const RemovePhoneLoginModal: React.FC<RemovePhoneLoginModalProps> = ({
  appKey,
  visible,
  setVisible,
  updatePhoneAction,
  setRefreshing,
  verifiedPhoneNumber,
  handleLogOut,
}): JSX.Element => {
  const { tokens } = useShellContext();
  const { targetProviders } = useShellAuthProviders();
  const { authProviderId } = useBlockPageContext();
  const { sizeClassV2 } = useSizeClass();

  const isMobileDevice = sizeClassV2 < SizeClassV2.M;

  const targetProviderToken = useMemo(
    () =>
      authProviderId
        ? tokens.accessTokens[authProviderId] || undefined
        : undefined,
    [authProviderId, tokens.accessTokens]
  );
  const decodedToken = useMemo(() => {
    if (!targetProviderToken) {
      return null;
    }

    return JSON.parse(
      decode(targetProviderToken.split('.')[1])
    ) as EndUserClaims;
  }, [targetProviderToken]);
  const isUserLoggedInWithPhone =
    decodedToken?.login_authproviderid === SUBSPLASH_PHONE_AUTH_PROVIDER_ID;

  const { t } = useTranslation(['account']);

  // ---------------------------------------------------------------------------
  // Global State

  const handleDeletePhoneNumber = async (): Promise<void> => {
    if (updatePhoneAction?.unlinkPhoneUrl) {
      const url = updatePhoneAction.unlinkPhoneUrl;

      const res = await BaseService.Delete({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const ok = res.status === 204 || res.status === 200;

      if (ok) {
        if (isUserLoggedInWithPhone) {
          setVisible(false);
          handleLogOut();
        } else {
          setRefreshing?.(true); // auto-refresh the entire screen to display new phone number if provided by backend
          notifyAppUserSettingsUpdated();
          setVisible(false);
          KitSnack.show(
            t('account:phoneNumberRemoved'),
            KitSnack.duration.SHORT
          );
        }
      } else {
        setVisible(false);
        KitSnack.show(t('account:errorMessage'), KitSnack.duration.LONG);
      }
    }
  };

  const renderContent = () => (
    <View
      style={{
        ...styles.container,
        ...(isMobileDevice
          ? { paddingTop: Spacing.xxl }
          : { paddingTop: Spacing.xl }),
      }}
    >
      <View style={styles.contentContainer}>
        <KitText h1 testID='RemovePhoneLoginModalTitle'>
          {t('account:removePhoneLoginModalTitle', {
            phoneNumber: formatPhoneNumberByRegion(
              verifiedPhoneNumber?.significant,
              verifiedPhoneNumber?.country?.region_code
            ),
          })}
        </KitText>
        {isUserLoggedInWithPhone ? (
          <KitText style={{ marginTop: Spacing.m }}>
            {t('account:removePhoneLoginModalSubtitle')}
          </KitText>
        ) : null}
      </View>
      <View
        style={{
          gap: Spacing.m,
          ...(!isMobileDevice && {
            flexDirection: 'row-reverse',
          }),
        }}
      >
        <KitButton
          color={Colors.R500}
          title={
            isUserLoggedInWithPhone
              ? t('account:yesRemoveAndLogout')
              : t('account:yesRemove')
          }
          onPress={handleDeletePhoneNumber}
        />
        <KitButton
          secondary
          title={t('account:removePhoneLoginCancelButton')}
          onPress={() => setVisible(false)}
        />
      </View>
    </View>
  );

  return (
    <KitModalV2
      anchorBottom={isMobileDevice}
      isVisible={visible}
      modalStyle={styles.modalStyle}
      backdropStyle={styles.backdropStyle}
      onClose={() => setVisible(false)}
      coverScreen
    >
      {renderContent()}
    </KitModalV2>
  );
};

const styles = StyleSheet.create({
  modalStyle: {
    width: '100%',
    maxWidth: 600,
    paddingTop: 0,
    paddingHorizontal: 0,
    paddingBottom: 0,
    alignSelf: 'center',
  },
  backdropStyle: {
    alignItems: undefined,
  },
  container: {
    paddingHorizontal: Spacing.xl,
    paddingBottom: Spacing.xl,
  },
  contentContainer: {
    marginBottom: Spacing.xl,
  },
});

export default RemovePhoneLoginModal;
